import { DEFAULT_RETRY_MUTATION } from '../config'
import { queryKeys } from '../keys'
import { transformBooking } from '../selects'
import type { QueryClient } from '@tanstack/react-query'
import type { GetBookingDetailResponse } from '@/types/booking'
import { paymentService } from '@/services'

function payBooking(queryClient: QueryClient) {
  return queryClient.setMutationDefaults(queryKeys.payment.pay(), {
    // A timed-out request may already have created a payment on the server.
    retry: 0,
    mutationFn: (payload: Parameters<typeof paymentService.pay>[0]) =>
      paymentService.pay(payload),
    onSuccess: (res) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.myBooking.tabList(),
      })
      if ('booking' in res.data && res.data.booking) {
        queryClient.setQueryData<GetBookingDetailResponse>(
          queryKeys.booking.detail(res.data.booking.id),
          (old) =>
            !old
              ? old
              : {
                  ...old,
                  data: {
                    ...old.data,
                    booking: transformBooking({
                      ...old.data.booking,
                      // @ts-expect-error
                      ...res.data.booking,
                    }),
                  },
                },
        )
        if (res.data.booking.adhoc_booking_booked_transportations.length) {
          Promise.all(
            res.data.booking.adhoc_booking_booked_transportations.map((bus) =>
              queryClient.invalidateQueries({
                queryKey: queryKeys.booking.transportation(bus.id),
              }),
            ),
          )
        }
      }
    },
  })
}

function retryPayBooking(queryClient: QueryClient) {
  return queryClient.setMutationDefaults(queryKeys.payment.retry(), {
    retry: 0,
    mutationFn: (payload: Parameters<typeof paymentService.retry>[0]) =>
      paymentService.retry(payload),
    onSuccess: (res) => {
      if ('booking' in res.data && res.data.booking) {
        queryClient.setQueryData<GetBookingDetailResponse>(
          queryKeys.booking.detail(res.data.booking.id),
          (old) =>
            !old
              ? old
              : {
                  ...old,
                  data: {
                    ...old.data,
                    booking: transformBooking({
                      ...old.data.booking,
                      // @ts-expect-error
                      ...res.data.booking,
                    }),
                  },
                },
        )
        if (res.data.booking.adhoc_booking_booked_transportations.length) {
          Promise.all(
            res.data.booking.adhoc_booking_booked_transportations.map((bus) =>
              queryClient.invalidateQueries({
                queryKey: queryKeys.booking.transportation(bus.id),
              }),
            ),
          )
        }
      }
    },
  })
}

function uploadProofOfPayment(queryClient: QueryClient) {
  return queryClient.setMutationDefaults(queryKeys.payment.uploadProof(), {
    retry: DEFAULT_RETRY_MUTATION,
    mutationFn: (payload: Parameters<typeof paymentService.uploadProof>[0]) =>
      paymentService.uploadProof(payload),
    onMutate: async (payload) => {
      await queryClient.cancelQueries({
        queryKey: queryKeys.booking.detail(payload.booking_id),
      })
      const existingBooking =
        queryClient.getQueryData<GetBookingDetailResponse>(
          queryKeys.booking.detail(payload.booking_id),
        )
      if (!existingBooking) return
      const optimisticBooking: GetBookingDetailResponse = {
        ...existingBooking,
        data: {
          ...existingBooking.data,
          booking: {
            ...existingBooking.data.booking,
            proof_of_bank_transfer: payload.proof_of_bank_transfer_id,
          },
        },
      }
      queryClient.setQueryData(
        queryKeys.booking.detail(payload.booking_id),
        optimisticBooking,
      )
      return { existingBooking, optimisticBooking }
    },
    onSuccess: (res, payload) => {
      queryClient.setQueryData<GetBookingDetailResponse>(
        queryKeys.booking.detail(payload.booking_id),
        (oldData) =>
          !oldData
            ? oldData
            : {
                ...oldData,
                data: {
                  ...oldData.data,
                  booking: {
                    ...oldData.data.booking,
                    proof_of_bank_transfer:
                      res.data.booking.proof_of_bank_transfer,
                  },
                },
              },
      )
    },
    onError: (_, payload, context) => {
      if (!context) return
      queryClient.setQueryData(
        queryKeys.booking.detail(payload.booking_id),
        context.existingBooking,
      )
    },
  })
}

export function registerPaymentMutations(queryClient: QueryClient) {
  payBooking(queryClient)
  retryPayBooking(queryClient)
  uploadProofOfPayment(queryClient)
}
